import React, { useMemo, useState, useSyncExternalStore } from 'react';
import { Checkbox } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Popover } from '../../Controls/Popover';
import { Input } from '../../Controls/Input';
import { useFilterBarContext } from '../state/FilterBarContext';

import cn from './styles.module.scss';

export interface AddFilterButtonProps {
  availableToAdd?: string[];
}

/**
 * Plan Section "Add filter (+) button":
 *   - Sort by `position` asc, then alphabetically by `label`.
 *   - Search input always shown.
 *   - Check adds field (full init); uncheck removes + clears value to canonical empty.
 *   - Menu stays open across check/uncheck.
 *   - Hidden when every configured item is pinned.
 */
export const AddFilterButton: React.FC<AddFilterButtonProps> = ({ availableToAdd }) => {
  const { store, labels, itemsByName } = useFilterBarContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const visibleFields = useSyncExternalStore(
    store.subscribe,
    () => store.getInternalState().visibleFields,
  );

  const candidates = useMemo(() => {
    const allowed = availableToAdd ? new Set(availableToAdd) : null;
    const items = Object.values(itemsByName)
      .filter((item) => item && !item.pinned)
      .filter((item) => !allowed || allowed.has(item.name));
    items.sort((a, b) => {
      const pa = a.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.position ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return a.label.localeCompare(b.label);
    });
    return items;
  }, [itemsByName, availableToAdd]);

  const filtered = useMemo(() => {
    if (!search) return candidates;
    const q = search.toLowerCase();
    return candidates.filter((i) => i.label.toLowerCase().includes(q));
  }, [candidates, search]);

  if (candidates.length === 0) return null;

  const content = (
    <div className={cn.addMenuRoot}>
      <Input
        className={cn.addMenuSearch}
        placeholder={labels.searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search filters"
        data-testid="add-filter-search"
      />
      <div className={cn.addMenuList}>
        {filtered.map((item) => {
          const isVisible = visibleFields.includes(item.name);
          return (
            <label key={item.name} className={cn.addMenuItem}>
              <Checkbox
                checked={isVisible}
                onChange={(e) => {
                  if (e.target.checked) {
                    store.addField(item.name);
                  } else {
                    store.removeField(item.name);
                  }
                }}
                data-testid={`add-filter-toggle-${item.name}`}
              />
              <span>{item.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title={labels.addFilter}
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
    >
      <button
        type="button"
        className={cn.addButton}
        aria-label={labels.addFilter}
        data-testid="add-filter-button"
      >
        <PlusOutlined />
      </button>
    </Popover>
  );
};
